"use client";

import Link from "@/infrastructure/router/Link";
import type { CSSProperties, ReactNode } from "react";
import { useCallback, useEffect, useState } from "react";
import { useArtistSession } from "@/components/auth/ArtistSessionProvider";
import {
	ArtistIcon,
	Avatar,
	EmptyState,
	GlassCard,
	NavigationBar,
	Skeleton,
	Toast,
} from "@/components/design-system/ArtistDesignSystem";
import {
	listLocalNotes,
	setLocalNoteLiked,
	type LocalNoteAsset,
} from "@/domain/social/LocalNoteStore";
import { offlineSocialRepository } from "@/domain/social/SocialRepository";

interface RenderedNote {
	readonly asset: LocalNoteAsset;
	readonly mediaURLs: readonly string[];
	readonly commentCount: number;
}

const SKELETON_IDS = ["note-skeleton-1", "note-skeleton-2", "note-skeleton-3"];

export function NoteFeed() {
	const auth = useArtistSession();
	const [notes, setNotes] = useState<readonly RenderedNote[] | null>(null);
	const [error, setError] = useState<string | null>(null);

	const load = useCallback(async () => {
		if (auth.phase !== "user" || auth.scope === null) {
			setNotes([]);
			return;
		}
		setNotes(null);
		setError(null);
		const scope = auth.scope;
		try {
			const assets = await listLocalNotes(scope);
			const rendered = await Promise.all(
				assets.map(async (asset): Promise<RenderedNote> => {
					const commentPage = await offlineSocialRepository.loadComments(
						scope,
						asset.id,
						0,
					);
					return {
						asset,
						mediaURLs: asset.media.map((media) => URL.createObjectURL(media.blob)),
						commentCount: commentPage.comments.length,
					};
				}),
			);
			setNotes(rendered);
		} catch {
			setNotes([]);
			setError("笔记暂时无法载入，请检查浏览器存储权限后重试。");
		}
	}, [auth.phase, auth.scope]);

	useEffect(() => {
		void load();
	}, [load]);

	useEffect(() => {
		return () => {
			notes?.forEach((note) => {
				note.mediaURLs.forEach((url) => {
					URL.revokeObjectURL(url);
				});
			});
		};
	}, [notes]);

	const toggleLike = useCallback(
		async (id: string) => {
			if (auth.scope === null) return;
			const current = notes?.find((note) => note.asset.id === id);
			if (current === undefined) return;
			const liked = !current.asset.isLiked;
			setNotes((items) =>
				items?.map((item) =>
					item.asset.id === id
						? {
								...item,
								asset: {
									...item.asset,
									isLiked: liked,
									likeCount: Math.max(
										0,
										item.asset.likeCount + (liked ? 1 : -1),
									),
								},
							}
						: item,
				) ?? null,
			);
			try {
				const saved = await setLocalNoteLiked(auth.scope, id, liked);
				setNotes((items) =>
					items?.map((item) =>
						item.asset.id === id ? { ...item, asset: saved } : item,
					) ?? null,
				);
			} catch {
				setError("点赞未保存，请重试。");
				void load();
			}
		},
		[auth.scope, load, notes],
	);

	if (auth.phase === "loading" || notes === null) return <NoteFeedSkeleton />;
	if (auth.phase !== "user" || auth.scope === null) {
		return (
			<section className="social-page note-feed-page" aria-label="浏览笔记">
				<NavigationBar title="笔记广场" backHref="/library" />
				<EmptyState
					title="登录后查看笔记"
					message="笔记与图片按账户隔离保存。"
					symbol={<ArtistIcon name="person" size={36} />}
				/>
				<Link className="primary-action" href="/auth">登录</Link>
			</section>
		);
	}

	return (
		<section className="social-page note-feed-page" aria-label="浏览笔记">
			<NavigationBar
				title="笔记广场"
				backHref="/library"
				trailing={
					<Link className="ds-icon-button" href="/note/compose" aria-label="写笔记">
						<ArtistIcon name="create" size={24} strokeWidth={1.9} />
					</Link>
				}
			/>
			{error === null ? null : <Toast style="error">{error}</Toast>}
			{notes.length === 0 ? (
				<>
					<EmptyState
						className="note-feed-empty"
						title="还没有笔记"
						message="写下灵感或采风片段，发布后会出现在这里。"
						symbol={<ArtistIcon name="message" size={36} />}
					/>
					<Link className="primary-action" href="/note/compose">写笔记</Link>
				</>
			) : (
				<ul className="note-feed-list" aria-label="笔记列表">
					{notes.map((note) => (
						<li key={note.asset.id} className="note-feed-list__item">
							<NoteCard note={note} onToggleLike={() => void toggleLike(note.asset.id)} />
						</li>
					))}
				</ul>
			)}
		</section>
	);
}

function NoteCard({
	note,
	onToggleLike,
}: Readonly<{ note: RenderedNote; onToggleLike: () => void }>) {
	const { asset, mediaURLs, commentCount } = note;
	return (
		<GlassCard className="note-feed-card" cornerRadius={16} style={{ padding: 16 }}>
			<header className="note-feed-card__header">
				<span className="note-feed-card__author">
					<Avatar name={asset.authorName} size={40} />
					<span className="note-feed-card__author-meta">
						<strong>{asset.authorName}</strong>
						<small><time dateTime={new Date(asset.createdAt).toISOString()}>{formatTimestamp(asset.createdAt)}</time></small>
					</span>
				</span>
				<VisibilityBadge visibility={asset.visibility} />
			</header>
			<Link
				className="note-feed-card__body"
				href={`/community/thread/${encodeURIComponent(asset.id)}`}
				aria-label="查看笔记详情"
			>
				<p className="note-feed-card__content">{asset.body}</p>
				{mediaURLs.length === 0 ? null : <NoteMediaGrid urls={mediaURLs} />}
			</Link>
			<footer className="note-feed-card__actions">
				<ActionButton
					label={asset.isLiked ? "取消点赞" : "喜欢"}
					icon={<ArtistIcon name="heart" size={20} strokeWidth={asset.isLiked ? 0 : 1.8} />}
					count={asset.likeCount}
					active={asset.isLiked}
					onClick={onToggleLike}
				/>
				<Link className="note-feed-action" href={`/community/thread/${encodeURIComponent(asset.id)}`} aria-label="查看评论">
					<ArtistIcon name="message" size={20} strokeWidth={1.8} />
					<span>{commentCount}</span>
				</Link>
				<ActionButton
					label="分享"
					icon={<ArtistIcon name="share" size={20} strokeWidth={1.8} />}
					count={null}
					onClick={() => {
						if (navigator.share) void navigator.share({ title: "Artist 笔记", text: asset.body }).catch(() => undefined);
					}}
				/>
			</footer>
		</GlassCard>
	);
}

function ActionButton({
	label,
	icon,
	count,
	active = false,
	onClick,
}: Readonly<{
	label: string;
	icon: ReactNode;
	count: number | null;
	active?: boolean;
	onClick: () => void;
}>) {
	return (
		<button
			type="button"
			className="note-feed-action"
			aria-label={label}
			onClick={onClick}
			style={active ? { color: "var(--ds-destructive)" } : undefined}
		>
			{icon}
			{count === null ? null : <span>{count}</span>}
		</button>
	);
}

function VisibilityBadge({ visibility }: Readonly<{ visibility: "private" | "public" }>) {
	const isPrivate = visibility === "private";
	const style: CSSProperties = {
		display: "inline-flex",
		alignItems: "center",
		gap: 4,
		padding: "4px 10px",
		borderRadius: "var(--ds-radius-full)",
		fontSize: "0.72rem",
		fontWeight: 600,
		color: isPrivate ? "var(--ds-text-secondary)" : "var(--ds-accent)",
		background: isPrivate
			? "color-mix(in display-p3, var(--ds-surface) 80%, transparent)"
			: "color-mix(in display-p3, var(--ds-accent) 18%, transparent)",
	};
	return (
		<span className="note-feed-badge" style={style}>
			<ArtistIcon name={isPrivate ? "shield" : "globe"} size={12} strokeWidth={1.8} />
			{isPrivate ? "私密" : "公开"}
		</span>
	);
}

function NoteMediaGrid({ urls }: Readonly<{ urls: readonly string[] }>) {
	return (
		<div
			className={urls.length === 1 ? "note-feed-media note-feed-media--single" : "note-feed-media note-feed-media--grid"}
			style={{ display: "grid", gridTemplateColumns: urls.length === 1 ? "1fr" : "1fr 1fr", gap: 4 }}
		>
			{urls.slice(0, 4).map((url, index) => (
				<div key={url} style={{ position: "relative", aspectRatio: "1 / 1", borderRadius: 8, overflow: "hidden", background: "var(--ds-surface)" }}>
					{/* biome-ignore lint/performance/noImgElement: owner-scoped IndexedDB Blob media. */}
					<img src={url} alt="" loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
					{index === 3 && urls.length > 4 ? <span className="note-feed-media__overflow">+{urls.length - 4}</span> : null}
				</div>
			))}
		</div>
	);
}

function formatTimestamp(epochMilliseconds: number): string {
	return new Intl.DateTimeFormat(undefined, {
		month: "short",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	}).format(new Date(epochMilliseconds));
}

function NoteFeedSkeleton() {
	return (
		<section className="social-page note-feed-page" aria-busy="true" aria-label="正在载入笔记">
			<NavigationBar title="笔记广场" backHref="/library" />
			<ul className="note-feed-list" aria-hidden="true">
				{SKELETON_IDS.map((id) => (
					<li key={id} className="note-feed-list__item">
						<GlassCard cornerRadius={16} style={{ padding: 16, display: "grid", gap: 12 }}>
							<div style={{ display: "flex", gap: 12, alignItems: "center" }}>
								<Skeleton shape="circle" width={40} height={40} />
								<Skeleton shape="rectangle" width={140} height={14} />
							</div>
							<Skeleton shape="rectangle" width="100%" height={72} />
						</GlassCard>
					</li>
				))}
			</ul>
		</section>
	);
}
